import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

export default function Questionnaire() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})

  const questions = [
    {
      id: 'q1',
      category: '腸の状態',
      question: '過去1週間の排便回数は平均して1日何回でしたか？',
      type: 'radio',
      options: [
        '1回未満',
        '1〜2回',
        '3〜4回',
        '5〜7回',
        '8回以上',
      ],
    },
    {
      id: 'q2',
      category: '腸の状態',
      question: '過去1週間で血便はありましたか？',
      type: 'radio',
      options: [
        'なし',
        'たまにあった（週1〜2回）',
        'しばしばあった（週3〜4回）',
        'ほぼ毎日あった',
      ],
    },
    {
      id: 'q3',
      category: '症状',
      question: '過去1週間で腹痛はどの程度ありましたか？',
      type: 'radio',
      options: [
        'なし',
        '軽度（日常生活に影響なし）',
        '中程度（日常生活に少し影響）',
        '重度（日常生活に大きく影響）',
      ],
    },
    {
      id: 'q4',
      category: '生活の質',
      question: '過去1週間の全体的な体調はいかがでしたか？',
      type: 'radio',
      options: [
        'とても良い',
        '良い',
        '普通',
        '悪い',
        'とても悪い',
      ],
    },
    {
      id: 'q5',
      category: '生活の質',
      question: '過去1週間で仕事や学校、日常活動にどの程度影響がありましたか？',
      type: 'radio',
      options: [
        '全く影響なし',
        'わずかに影響あり',
        'ある程度影響あり',
        'かなり影響あり',
        '全くできなかった',
      ],
    },
    {
      id: 'q6',
      category: 'その他',
      question: '治療について何かご質問やご要望はありますか？',
      type: 'textarea',
    },
  ]

  const currentQuestion = questions[currentStep]
  const totalSteps = questions.length
  const progress = ((currentStep + 1) / totalSteps) * 100

  const handleAnswer = (value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }))
  }

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = () => {
    console.log('Questionnaire answers:', answers)
    // TODO: API送信
    navigate('/patient')
  }

  return (
    <div className="questionnaire-page">
      <header className="page-header">
        <Link to="/patient" className="back-button">
          <span className="material-symbols-outlined">arrow_back</span>
        </Link>
        <h1 className="page-title">定期アンケート</h1>
      </header>

      {/* 進捗バー */}
      <div className="progress-container">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="progress-text">
          {currentStep + 1} / {totalSteps}
        </span>
      </div>

      {/* 質問カード */}
      <div className="question-card">
        <span className="question-category">{currentQuestion.category}</span>
        <h2 className="question-text">{currentQuestion.question}</h2>

        <div className="answer-section">
          {currentQuestion.type === 'radio' && currentQuestion.options && (
            <div className="options-list">
              {currentQuestion.options.map((option, index) => (
                <label
                  key={index}
                  className={`option-button ${answers[currentQuestion.id] === option ? 'option-button--selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={currentQuestion.id}
                    value={option}
                    checked={answers[currentQuestion.id] === option}
                    onChange={(e) => handleAnswer(e.target.value)}
                  />
                  <span className="option-text">{option}</span>
                </label>
              ))}
            </div>
          )}

          {currentQuestion.type === 'textarea' && (
            <textarea
              className="answer-textarea"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              rows={5}
              placeholder="ご自由にお書きください"
            />
          )}
        </div>
      </div>

      {/* ナビゲーション */}
      <div className="navigation">
        <button
          className="nav-button nav-button--prev"
          onClick={handlePrev}
          disabled={currentStep === 0}
        >
          <span className="material-symbols-outlined">chevron_left</span>
          前へ
        </button>

        {currentStep < totalSteps - 1 ? (
          <button
            className="nav-button nav-button--next"
            onClick={handleNext}
            disabled={!answers[currentQuestion.id]}
          >
            次へ
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        ) : (
          <button
            className="nav-button nav-button--submit"
            onClick={handleSubmit}
          >
            <span className="material-symbols-outlined">send</span>
            送信する
          </button>
        )}
      </div>

      <style>{`
        .questionnaire-page {
          max-width: 600px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-lg);
        }

        .back-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: var(--background-primary);
          color: var(--text-secondary);
          box-shadow: var(--shadow-sm);
        }

        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
        }

        .progress-container {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-lg);
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background-color: var(--color-solid-gray100);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 0.875rem;
          color: var(--text-secondary);
          min-width: 60px;
          text-align: right;
        }

        .question-card {
          background-color: var(--background-primary);
          padding: var(--spacing-xl);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
          margin-bottom: var(--spacing-lg);
        }

        .question-category {
          display: inline-block;
          padding: 4px 12px;
          background-color: #f0fdfa;
          color: #0891b2;
          border-radius: var(--border-radius-sm);
          font-size: 0.75rem;
          font-weight: 500;
          margin-bottom: var(--spacing-md);
        }

        .question-text {
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: var(--spacing-lg);
          line-height: 1.5;
        }

        .answer-section {
          margin-top: var(--spacing-md);
        }

        .options-list {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-sm);
        }

        .option-button {
          display: flex;
          align-items: center;
          padding: var(--spacing-md);
          border: 2px solid var(--border-color);
          border-radius: var(--border-radius-md);
          cursor: pointer;
          transition: all 0.2s;
        }

        .option-button:hover {
          border-color: #0891b2;
        }

        .option-button--selected {
          border-color: #0891b2;
          background-color: #f0fdfa;
        }

        .option-button input {
          display: none;
        }

        .option-text {
          font-size: 0.875rem;
        }

        .answer-textarea {
          width: 100%;
          padding: var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          resize: vertical;
        }

        .answer-textarea:focus {
          outline: none;
          border-color: #0891b2;
          box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.1);
        }

        .navigation {
          display: flex;
          justify-content: space-between;
          gap: var(--spacing-md);
        }

        .nav-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-sm) var(--spacing-lg);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .nav-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .nav-button--prev {
          background-color: var(--background-primary);
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
        }

        .nav-button--next {
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          border: none;
          color: white;
        }

        .nav-button--next:disabled {
          background: var(--color-solid-gray200);
        }

        .nav-button--submit {
          background: linear-gradient(135deg, #059669 0%, #10b981 100%);
          border: none;
          color: white;
        }

        .nav-button--submit:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }
      `}</style>
    </div>
  )
}



